import { FormEvent, useEffect, useState } from "react";
import toast from "react-hot-toast";

import type { PolicyLookupCompanyInformation } from "@platform/dmv-api";

import { invalidateClientConfigCache } from "../../lib/automatic";
import { updatePolicyLookupCompanyInfo } from "../../lib/policyLookupSettings";
import ButtonPrimary from "../ButtonPrimary";
import { Address, AddressFields, emptyAddress } from "./AddressFields";
import FieldLabel from "./FieldLabel";
import SettingsSection from "./SettingsSection";

const inputClassName =
  "w-full rounded border border-gray-300 p-2.5 text-sm placeholder-gray-400 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-black disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-500";

type FormState = {
  businessName: string;
  businessAddress: Address;
  lenderName: string;
  lenderAddress: Address;
  firstName: string;
  lastName: string;
  email: string;
  ein: string;
};

const emptyForm: FormState = {
  businessName: "",
  businessAddress: emptyAddress,
  lenderName: "",
  lenderAddress: emptyAddress,
  firstName: "",
  lastName: "",
  email: "",
  ein: "",
};

const toFormState = (
  companyInformation: PolicyLookupCompanyInformation | null
): FormState => {
  if (!companyInformation) return emptyForm;

  return {
    businessName: companyInformation.businessName ?? "",
    businessAddress: {
      ...emptyAddress,
      ...companyInformation.businessAddress,
      addressLine2: companyInformation.businessAddress?.addressLine2 ?? "",
    },
    lenderName: companyInformation.lenderName ?? "",
    lenderAddress: {
      ...emptyAddress,
      ...companyInformation.lenderAddress,
      addressLine2: companyInformation.lenderAddress?.addressLine2 ?? "",
    },
    firstName: companyInformation.lienholderPortalAdmin?.firstName ?? "",
    lastName: companyInformation.lienholderPortalAdmin?.lastName ?? "",
    email: companyInformation.lienholderPortalAdmin?.email ?? "",
    ein: companyInformation.ein ?? "",
  };
};

const CompanyInformationSection = ({
  clientId,
  initialCompanyInformation,
  id,
}: {
  clientId: string;
  initialCompanyInformation: PolicyLookupCompanyInformation | null;
  id?: string;
}) => {
  const [form, setForm] = useState<FormState>(
    toFormState(initialCompanyInformation)
  );
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setForm(toFormState(initialCompanyInformation));
  }, [initialCompanyInformation]);

  const lenderAddressStarted =
    form.lenderAddress.addressLine1.trim() !== "" ||
    form.lenderAddress.addressLine2.trim() !== "" ||
    form.lenderAddress.city.trim() !== "" ||
    form.lenderAddress.state.trim() !== "" ||
    form.lenderAddress.postalCode.trim() !== "" ||
    form.lenderAddress.country.trim() !== "";

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      const companyInformation: PolicyLookupCompanyInformation = {
        businessName: form.businessName,
        businessAddress: {
          ...form.businessAddress,
          addressLine2: form.businessAddress.addressLine2 || null,
        },
        ...(form.lenderName || lenderAddressStarted
          ? {
              lenderName: form.lenderName || undefined,
              lenderAddress: lenderAddressStarted
                ? {
                    ...form.lenderAddress,
                    addressLine2: form.lenderAddress.addressLine2 || null,
                  }
                : undefined,
            }
          : {}),
        lienholderPortalAdmin: {
          firstName: form.firstName,
          lastName: form.lastName,
          email: form.email,
        },
        ein: form.ein,
      };

      const [, error] = await updatePolicyLookupCompanyInfo(
        clientId,
        companyInformation
      );
      if (error) {
        toast.error(error.message || "Could not save company information");
        return;
      }

      invalidateClientConfigCache(clientId, ["policyLookup"]);
      toast.success("Company information updated");
    } catch (err: any) {
      toast.error(err?.message || "Something went wrong");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <SettingsSection
      id={id}
      title="Company Information"
      subtitle="Used to register your business with lienholder portals for our Policy Lookup feature."
    >
      <form onSubmit={handleSubmit} className="flex flex-col gap-y-6">
        <div className="flex flex-col gap-y-2">
          <FieldLabel required>Business name</FieldLabel>
          <input
            className={inputClassName}
            value={form.businessName}
            onChange={(e) => setForm({ ...form, businessName: e.target.value })}
            required
          />
        </div>

        <div className="flex flex-col gap-y-2">
          <FieldLabel required>EIN</FieldLabel>
          <input
            className={inputClassName}
            value={form.ein}
            onChange={(e) => setForm({ ...form, ein: e.target.value })}
            required
          />
        </div>

        <div className="flex flex-col gap-y-3">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">
              Business address
            </h3>
            <p className="text-xs text-gray-500 mt-1">
              The registered address used when creating your lienholder portal
              accounts.
            </p>
          </div>
          <AddressFields
            address={form.businessAddress}
            onChange={(businessAddress) =>
              setForm({ ...form, businessAddress })
            }
          />
        </div>

        <div className="flex flex-col gap-y-3">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">
              Lender (optional)
            </h3>
            <p className="text-xs text-gray-500 mt-1">
              If a specific lender should be listed on your lienholder portal
              registrations, provide their name and address here.
            </p>
          </div>
          <div className="flex flex-col gap-y-2">
            <FieldLabel>Lender name</FieldLabel>
            <input
              className={inputClassName}
              value={form.lenderName}
              onChange={(e) => setForm({ ...form, lenderName: e.target.value })}
            />
          </div>
          <AddressFields
            address={form.lenderAddress}
            onChange={(lenderAddress) => setForm({ ...form, lenderAddress })}
            required={lenderAddressStarted}
          />
        </div>

        <div className="flex flex-col gap-y-3">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">
              Lienholder portal admin
            </h3>
            <p className="text-xs text-gray-500 mt-1">
              The point of contact listed as the account admin on your
              lienholder portal registrations.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="flex flex-col gap-y-2">
              <FieldLabel required>First name</FieldLabel>
              <input
                className={inputClassName}
                value={form.firstName}
                onChange={(e) =>
                  setForm({ ...form, firstName: e.target.value })
                }
                required
              />
            </div>
            <div className="flex flex-col gap-y-2">
              <FieldLabel required>Last name</FieldLabel>
              <input
                className={inputClassName}
                value={form.lastName}
                onChange={(e) => setForm({ ...form, lastName: e.target.value })}
                required
              />
            </div>
            <div className="flex flex-col gap-y-2 col-span-2">
              <FieldLabel required>Email</FieldLabel>
              <input
                type="email"
                className={inputClassName}
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                required
              />
            </div>
          </div>
        </div>

        <div className="flex justify-end">
          <ButtonPrimary
            text={isSaving ? "Saving…" : "Save"}
            type="submit"
            disabled={isSaving}
            icon={undefined}
            className=""
          />
        </div>
      </form>
    </SettingsSection>
  );
};

export default CompanyInformationSection;
